import React from 'react';
import { cn } from '../../../lib/utils';
import type { AccountMailboxFolder } from '../useData';

export type MailboxFolderAccent =
  | 'inbox'
  | 'sent'
  | 'mySent'
  | 'junk'
  | 'mentions'
  | 'drafts'
  | 'archived'
  | 'library'
  | 'outbox';

type AccentTokens = {
  iconDefault: string;
  iconSelected: string;
  borderSelected: string;
  hoverRow: string;
};

const FOLDER_ACCENTS: Record<MailboxFolderAccent, AccentTokens> = {
  inbox: {
    iconDefault: 'bg-sky-100 text-sky-600 dark:bg-sky-950/55 dark:text-sky-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-sky-400',
    hoverRow: 'hover:bg-sky-50/70 dark:hover:bg-sky-950/25',
  },
  sent: {
    iconDefault: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/55 dark:text-emerald-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-emerald-400',
    hoverRow: 'hover:bg-emerald-50/70 dark:hover:bg-emerald-950/25',
  },
  mySent: {
    iconDefault: 'bg-teal-100 text-teal-600 dark:bg-teal-950/55 dark:text-teal-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-teal-400',
    hoverRow: 'hover:bg-teal-50/70 dark:hover:bg-teal-950/25',
  },
  junk: {
    iconDefault: 'bg-amber-100 text-amber-700 dark:bg-amber-950/55 dark:text-amber-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-amber-400',
    hoverRow: 'hover:bg-amber-50/70 dark:hover:bg-amber-950/25',
  },
  mentions: {
    iconDefault: 'bg-violet-100 text-violet-600 dark:bg-violet-950/55 dark:text-violet-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-violet-400',
    hoverRow: 'hover:bg-violet-50/70 dark:hover:bg-violet-950/25',
  },
  drafts: {
    iconDefault: 'bg-slate-200/80 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-slate-400',
    hoverRow: 'hover:bg-slate-100/80 dark:hover:bg-slate-800/40',
  },
  archived: {
    iconDefault: 'bg-stone-200/90 text-stone-600 dark:bg-stone-800/80 dark:text-stone-300',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-stone-400',
    hoverRow: 'hover:bg-stone-100/80 dark:hover:bg-stone-900/40',
  },
  library: {
    iconDefault: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/55 dark:text-indigo-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-indigo-400',
    hoverRow: 'hover:bg-indigo-50/70 dark:hover:bg-indigo-950/25',
  },
  outbox: {
    iconDefault: 'bg-orange-100 text-orange-600 dark:bg-orange-950/55 dark:text-orange-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-orange-400',
    hoverRow: 'hover:bg-orange-50/70 dark:hover:bg-orange-950/25',
  },
};

export function accentFromMailboxFolder(folder: AccountMailboxFolder): MailboxFolderAccent {
  switch (folder) {
    case 'INBOX':
      return 'inbox';
    case 'SENT':
      return 'sent';
    case 'MY_SENT':
      return 'mySent';
    case 'JUNK':
      return 'junk';
    default:
      return 'drafts';
  }
}

export function mailboxFolderRowSelected(kind: MailboxFolderAccent, rowSelectedBase: string): string {
  return cn(rowSelectedBase, FOLDER_ACCENTS[kind].borderSelected);
}

export function mailboxFolderRowDefault(kind: MailboxFolderAccent, rowDefault: string): string {
  return cn(rowDefault, FOLDER_ACCENTS[kind].hoverRow);
}

export function MailboxFolderIcon({
  kind,
  selected,
  children,
}: {
  kind: MailboxFolderAccent;
  selected: boolean;
  children: React.ReactNode;
}) {
  const accent = FOLDER_ACCENTS[kind];
  return (
    <span
      className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors [&_svg]:h-4 [&_svg]:w-4',
        selected ? accent.iconSelected : accent.iconDefault,
      )}
      aria-hidden
    >
      {children}
    </span>
  );
}
